import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Send, Eye, AlertCircle } from 'lucide-react';
import { FullPageLoader, LoadingSpinner } from '../components/LoadingSpinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SMS() {
  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [driverTemplate, setDriverTemplate] = useState('');
  const [passengerTemplate, setPassengerTemplate] = useState('');
  const [unallocatedTemplate, setUnallocatedTemplate] = useState('');
  const [previews, setPreviews] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendTo, setSendTo] = useState('all');
  const [showDonationPopup, setShowDonationPopup] = useState(false);
  const [pendingSendAction, setPendingSendAction] = useState(null);
  const { tripId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // OPTIMIZATION: Fetch data in parallel and set default templates
  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Parallel API calls for faster loading
      const [tripRes, partsRes, groupsRes] = await Promise.all([
        axios.get(`${API_URL}/trips/${tripId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips/${tripId}/participants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips/${tripId}/groups`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTrip(tripRes.data);
      setParticipants(partsRes.data);
      setGroups(groupsRes.data);
      
      // Set default templates after data is loaded
      setDefaultTemplates();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const setDefaultTemplates = () => {
    const organizerPhone = user?.verified_phone_number || '[phone]';
    
    setDriverTemplate(
      `Hi {{driver_name}}, you're driving for {{group_name}}. Your passengers are:\n\n{{passenger_list}}\n\nText me at ${organizerPhone} if you have questions since this number isn't monitored.`
    );
    
    setPassengerTemplate(
      `Hi {{passenger_name}}, your driver is {{driver_name}} ({{driver_phone}}). Pickup at {{pickup_address}}.\n\nText me at ${organizerPhone} if you have questions since this number isn't monitored.`
    );
    
    setUnallocatedTemplate(
      `Hi {{first_name}}, you are currently unallocated for the trip. Please contact me at ${organizerPhone} for more information.`
    );
  };

  const generatePreviews = async (recipientFilter = 'all', showDialog = true) => {
    try {
      const response = await axios.post(
        `${API_URL}/sms/preview`,
        {
          trip_id: tripId,
          driver_template: driverTemplate,
          passenger_template: passengerTemplate,
          unallocated_template: unallocatedTemplate,
          send_to: recipientFilter
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPreviews(response.data);
      if (showDialog) {
        setShowPreview(true);
      }
    } catch (error) {
      toast.error('Failed to generate previews');
    }
  };

  const handleSendClick = (testMode = false, recipientFilter = 'all') => {
    // Generate previews silently (for cost calculation) without showing preview dialog
    generatePreviews(recipientFilter, false);
    
    // Store the pending send action
    setPendingSendAction({ testMode, recipientFilter });
    
    // Show donation popup
    setShowDonationPopup(true);
  };

  const proceedWithSend = async () => {
    if (!pendingSendAction) return;
    
    const { testMode, recipientFilter } = pendingSendAction;
    const recipientText = recipientFilter === 'all' ? 'all' : 
                         recipientFilter === 'drivers' ? 'only drivers' :
                         recipientFilter === 'passengers' ? 'only passengers' : 'only unallocated people';
    
    if (!window.confirm(testMode ? 'Send test SMS to your phone?' : `Send SMS to ${recipientText} (${previews.length} recipients)?`)) {
      setShowDonationPopup(false);
      setPendingSendAction(null);
      return;
    }

    setSending(true);
    setShowDonationPopup(false);
    
    try {
      const response = await axios.post(
        `${API_URL}/sms/send`,
        {
          trip_id: tripId,
          driver_template: driverTemplate,
          passenger_template: passengerTemplate,
          unallocated_template: unallocatedTemplate,
          send_to: recipientFilter,
          test_mode: testMode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      if (!testMode) {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
      setPendingSendAction(null);
    }
  };

  const handleDonateNow = () => {
    // Open donation link in new tab
    window.open('https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00', '_blank');
    // Proceed with sending in current tab
    proceedWithSend();
  };

  const handleMaybeLater = () => {
    // Just proceed with sending
    proceedWithSend();
  };

  // OPTIMIZATION: Memoize expensive calculations
  const { drivers, passengers, driverPreviews, passengerPreviews, unallocatedPreviews, totalChars, maxChars, estimatedCost } = useMemo(() => {
    const driversFiltered = participants.filter(p => p.role === 'driver');
    const passengersFiltered = participants.filter(p => p.role === 'passenger');
    const driverPreviewsFiltered = previews.filter(p => p.recipient_type === 'driver');
    const passengerPreviewsFiltered = previews.filter(p => p.recipient_type === 'passenger');
    const unallocatedPreviewsFiltered = previews.filter(p => p.recipient_type === 'unallocated');
    const total = previews.reduce((sum, p) => sum + p.char_count, 0);
    const max = Math.max(...previews.map(p => p.char_count), 0);
    const cost = (previews.length * 0.15).toFixed(2);
    
    return {
      drivers: driversFiltered,
      passengers: passengersFiltered,
      driverPreviews: driverPreviewsFiltered,
      passengerPreviews: passengerPreviewsFiltered,
      unallocatedPreviews: unallocatedPreviewsFiltered,
      totalChars: total,
      maxChars: max,
      estimatedCost: cost
    };
  }, [participants, previews]);

  if (loading) {
    return <FullPageLoader text="Loading SMS templates..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {trip?.name || 'Trip'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">Step 4: SMS Preview & Send</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/trip/${tripId}/allocate`)}
                data-testid="back-to-allocate"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-sms"
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {groups.length === 0 && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm">
                  <strong>Warning:</strong> No groups allocated yet. Go back to the Allocate page to create groups first.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle>Driver SMS Template</CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                {drivers.length} drivers will receive this message
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="driver-template">Message Template</Label>
                  <Textarea
                    id="driver-template"
                    value={driverTemplate}
                    onChange={(e) => setDriverTemplate(e.target.value)}
                    rows={8}
                    className="mt-1 font-mono text-sm"
                    data-testid="driver-template-input"
                  />
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p><strong>Available variables:</strong></p>
                  <p>• driver_name, driver_first_name, driver_last_name</p>
                  <p>• driver_phone, group_name</p>
                  <p>• passenger_list (formatted list)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Passenger SMS Template</CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                {passengers.length} passengers will receive this message
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="passenger-template">Message Template</Label>
                  <Textarea
                    id="passenger-template"
                    value={passengerTemplate}
                    onChange={(e) => setPassengerTemplate(e.target.value)}
                    rows={8}
                    className="mt-1 font-mono text-sm"
                    data-testid="passenger-template-input"
                  />
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p><strong>Available variables:</strong></p>
                  <p>• passenger_name, passenger_first_name, passenger_last_name</p>
                  <p>• passenger_phone, driver_name, driver_phone</p>
                  <p>• pickup_address, group_name</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Unallocated SMS Template</CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                {participants.filter(p => !p.group_id).length} unallocated people
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="unallocated-template">Message Template</Label>
                  <Textarea
                    id="unallocated-template"
                    value={unallocatedTemplate}
                    onChange={(e) => setUnallocatedTemplate(e.target.value)}
                    rows={8}
                    className="mt-1 font-mono text-sm"
                    data-testid="unallocated-template-input"
                  />
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p><strong>Available variables:</strong></p>
                  <p>• name, first_name</p>
                  <p>• role, phone, address</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="space-y-4">
              {/* Preview Button */}
              <div>
                <Button
                  onClick={() => generatePreviews('all', true)}
                  data-testid="preview-sms-button"
                  variant="outline"
                  className="w-full"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview All Messages
                </Button>
              </div>

              {/* Send Buttons */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Button
                  onClick={() => handleSendClick(false, 'all')}
                  disabled={sending}
                  data-testid="send-all-button"
                >
                  {sending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to All
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => handleSendClick(false, 'drivers')}
                  disabled={sending || drivers.length === 0}
                  variant="outline"
                  data-testid="send-drivers-button"
                >
                  {sending ? <LoadingSpinner size="sm" /> : `Send to Drivers (${drivers.length})`}
                </Button>
                
                <Button
                  onClick={() => handleSendClick(false, 'passengers')}
                  disabled={sending || passengers.length === 0}
                  variant="outline"
                  data-testid="send-passengers-button"
                >
                  {sending ? <LoadingSpinner size="sm" /> : `Send to Passengers (${passengers.length})`}
                </Button>
                
                <Button
                  onClick={() => handleSendClick(false, 'unallocated')}
                  disabled={sending || participants.filter(p => !p.group_id).length === 0}
                  variant="outline"
                  data-testid="send-unallocated-button"
                >
                  {sending ? <LoadingSpinner size="sm" /> : `Send to Unallocated (${participants.filter(p => !p.group_id).length})`}
                </Button>
              </div>

              {/* Test Send Button */}
              <div>
                <Button
                  onClick={() => handleSendClick(true, 'all')}
                  disabled={sending}
                  variant="secondary"
                  className="w-full"
                  data-testid="test-send-button"
                >
                  Send Test to My Phone
                </Button>
              </div>
            </div>

            {previews.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Total Messages</p>
                    <p className="text-2xl font-bold text-blue-900">{previews.length}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Longest Message</p>
                    <p className="text-2xl font-bold text-blue-900">{maxChars} chars</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Est. Cost (AUD)</p>
                    <p className="text-2xl font-bold text-blue-900">${estimatedCost}</p>
                  </div>
                </div>
                {maxChars > 918 && (
                  <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Warning: Some messages exceed 918 characters and may be truncated</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Message Previews</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {driverPreviews.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Driver Messages ({driverPreviews.length})</h3>
                  <div className="space-y-3">
                    {driverPreviews.map((preview, idx) => (
                      <Card key={idx}>
                        <CardContent className="py-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{preview.recipient_name}</p>
                              <p className="text-sm text-slate-600">{preview.phone}</p>
                            </div>
                            <span className="text-xs text-slate-500">{preview.char_count} chars</span>
                          </div>
                          <pre className="text-sm whitespace-pre-wrap font-sans bg-slate-50 p-3 rounded border">
                            {preview.message}
                          </pre>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {passengerPreviews.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Passenger Messages ({passengerPreviews.length})</h3>
                  <div className="space-y-3">
                    {passengerPreviews.map((preview, idx) => (
                      <Card key={idx}>
                        <CardContent className="py-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{preview.recipient_name}</p>
                              <p className="text-sm text-slate-600">{preview.phone}</p>
                            </div>
                            <span className="text-xs text-slate-500">{preview.char_count} chars</span>
                          </div>
                          <pre className="text-sm whitespace-pre-wrap font-sans bg-slate-50 p-3 rounded border">
                            {preview.message}
                          </pre>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {unallocatedPreviews.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Unallocated Messages ({unallocatedPreviews.length})</h3>
                  <div className="space-y-3">
                    {unallocatedPreviews.map((preview, idx) => (
                      <Card key={idx}>
                        <CardContent className="py-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{preview.recipient_name}</p>
                              <p className="text-sm text-slate-600">{preview.phone}</p>
                            </div>
                            <span className="text-xs text-slate-500">{preview.char_count} chars</span>
                          </div>
                          <pre className="text-sm whitespace-pre-wrap font-sans bg-slate-50 p-3 rounded border">
                            {preview.message}
                          </pre>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Donation Popup - Glassmorphic Design */}
        <Dialog open={showDonationPopup} onOpenChange={setShowDonationPopup}>
          <DialogContent className="max-w-md border-0 bg-transparent shadow-none">
            <div 
              className="relative overflow-hidden rounded-2xl p-8"
              style={{
                background: 'rgba(174, 214, 241, 0.7)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.5)',
              }}
            >
              <div className="text-center space-y-6">
                <h2 className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Support Nomad 💙
                </h2>
                
                <p className="text-slate-700 leading-relaxed">
                  Hi <strong>{user?.name || 'there'}</strong>, thanks for using Nomad to organise your roadtrip. 
                  While Nomad is a free service, each SMS costs 15c. Please consider donating to cover this, 
                  as well as to cover other users who can't afford to donate.
                </p>

                <div className="flex flex-col gap-3 pt-4">
                  <Button
                    onClick={handleDonateNow}
                    className="w-full text-white"
                    style={{
                      background: 'rgba(37, 99, 235, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                    data-testid="donate-now-button"
                  >
                    Donate Now
                  </Button>
                  
                  <Button
                    onClick={handleMaybeLater}
                    variant="outline"
                    className="w-full"
                    style={{
                      background: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                    data-testid="maybe-later-button"
                  >
                    Maybe Later
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>

      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}
